import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Stack, Input, Select, SelectableOption, Text } from '@moda/om';
import { UserApiMutationError } from '../../../../lib/createUserApiError';
import { COUNTRIES, STATES_OR_REGIONS } from '../../../../constants';
import { FormErrors } from '../../../../hooks/useForm';
import {
  RestAddress,
  RestAddressAttributes,
  useAddressAutocomplete
} from '../../../../hooks/useAddress';
import { useCountry } from '../../../CountrySelector';
import { getAttributesFromPlace } from './getAttributesFromPlace';

import './ShippingAddressFormFields.scss';

export const COUNTRY_OPTIONS: SelectableOption[] = COUNTRIES.map(country => ({
  label: country.name,
  value: country.alpha2Code
}));

const BLOCK_NAME = 'ShippingAddressFormFields';

const getStateOrRegionOptions = (
  selectedCountryOrRegion: string | undefined
): SelectableOption[] | undefined =>
  selectedCountryOrRegion
    ? STATES_OR_REGIONS[selectedCountryOrRegion]?.map(({ name }) => ({
        label: name,
        value: name
      }))
    : undefined;

interface Props {
  version?: 'full' | 'compact';
  address?: RestAddress;
  mutationFieldErrors: UserApiMutationError | undefined;
  validationErrors: FormErrors;
}

export const ShippingAddressFormFields: React.FC<Props> = ({
  version = 'full',
  address,
  mutationFieldErrors,
  validationErrors
}) => {
  const countryCode = useCountry().country.alpha2Code;

  const [addressAttributes, setAddressAttributes] = useState<RestAddressAttributes>(
    address?.attributes || {
      __typename: 'RestAddressAttributes',
      source: 'moda',
      countryCode,
      firstName: '',
      lastName: '',
      phone: '',
      state: STATES_OR_REGIONS[countryCode] ? STATES_OR_REGIONS[countryCode][0].name : '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: ''
    }
  );

  const addressRef = useRef<HTMLInputElement>(null);
  const { place, isFocused } = useAddressAutocomplete(addressRef, addressAttributes.countryCode);

  const stateOrRegionOptions = useMemo(
    () => getStateOrRegionOptions(addressAttributes.countryCode),
    [addressAttributes.countryCode]
  );

  const handleChange = useCallback((field: keyof RestAddressAttributes, value: string) => {
    setAddressAttributes(attributes => ({
      ...attributes,
      [field]: value
    }));
  }, []);

  const handleCountryChange = useCallback((value: string) => {
    setAddressAttributes(attributes => ({
      ...attributes,
      countryCode: value,
      state: STATES_OR_REGIONS[value] ? STATES_OR_REGIONS[value][0].name : ''
    }));
  }, []);

  useEffect(() => {
    if (place) {
      setAddressAttributes(attributes => ({
        ...attributes,
        ...getAttributesFromPlace(place)
      }));
    }
  }, [place]);

  return (
    <div className={`${BLOCK_NAME}__fields`}>
      <Input label="Country or Region" name="countryCode">
        <Select
          idRef="CountryOrRegionDropdown"
          label=""
          onChange={handleCountryChange}
          options={COUNTRY_OPTIONS}
          value={addressAttributes.countryCode}
        />
      </Input>

      {version === 'full' && (
        <div className={`${BLOCK_NAME}__group`}>
          <Input
            className={`${BLOCK_NAME}__field`}
            error={validationErrors.firstName?.message || mutationFieldErrors?.['first_name']}
            label="First Name*"
            name="firstName"
            onChange={({ target }) => handleChange('firstName', target.value)}
            value={addressAttributes.firstName}
            maxLength={50}
          />
          <Input
            className={`${BLOCK_NAME}__field`}
            error={validationErrors.lastName?.message}
            label="Last Name*"
            name="lastName"
            onChange={({ target }) => handleChange('lastName', target.value)}
            value={addressAttributes.lastName}
            maxLength={50}
          />
        </div>
      )}

      <Input
        error={validationErrors.addressLine1?.message || mutationFieldErrors?.['address_line1']}
        label="Address*"
        name="addressLine1"
        onChange={({ target }) => handleChange('addressLine1', target.value)}
        onKeyPress={event => event.key === 'Enter' && isFocused && event.preventDefault()}
        ref={addressRef}
        value={addressAttributes.addressLine1}
      />

      <Input
        error={mutationFieldErrors?.['address_line2']}
        label="Address Line 2(Floor, Apt #)"
        name="addressLine2"
        onChange={({ target }) => handleChange('addressLine2', target.value)}
        value={addressAttributes.addressLine2}
      />

      <div className={`${BLOCK_NAME}__group`}>
        <Input
          className={`${BLOCK_NAME}__field`}
          error={validationErrors.city?.message || mutationFieldErrors?.city}
          label="City"
          name="city"
          onChange={({ target }) => handleChange('city', target.value)}
          value={addressAttributes.city}
        />

        <Input
          className={`${BLOCK_NAME}__field`}
          error={validationErrors.state?.message || mutationFieldErrors?.state}
          label="State or Region"
          name="state"
          onChange={({ target }) => handleChange('state', target.value)}
          value={addressAttributes.state}
        >
          {stateOrRegionOptions && (
            <Select
              idRef="StateOrRegionDropdown"
              error={validationErrors.state?.message || mutationFieldErrors?.state}
              label=""
              onChange={value => handleChange('state', value)}
              options={stateOrRegionOptions}
              value={stateOrRegionOptions.find(s => s.label === addressAttributes.state)?.value}
            />
          )}
        </Input>

        <Input
          className={`${BLOCK_NAME}__field`}
          error={validationErrors.postalCode?.message || mutationFieldErrors?.['postal_code']}
          label="Zip/Postal"
          name="postalCode"
          onChange={({ target }) => handleChange('postalCode', target.value)}
          value={addressAttributes.postalCode}
        />
      </div>

      {version === 'full' && (
        <Stack space={1}>
          <Input
            error={validationErrors['phone']?.message || mutationFieldErrors?.phone}
            label="Phone Number"
            name="phone"
            onChange={({ target }) => handleChange('phone', target.value)}
            value={addressAttributes.phone}
          />
          <Text color="cement">Only used in case of delivery issues</Text>
        </Stack>
      )}
    </div>
  );
};
